import { useEffect, useRef, useState, type RefObject, type ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  BookOpenCheck,
  CircleHelp,
  Home,
  LibraryBig,
  LogOut,
  Menu,
  Moon,
  Plus,
  Settings,
  Images,
  ShieldCheck,
  Sun,
  Users,
  X,
} from 'lucide-react';
import { api } from './api';
import { useAppStore } from './store';

export function Logo({ inverse = false, to = '/' }: { inverse?: boolean; to?: string }) {
  return (
    <Link
      to={to}
      className={`inline-flex items-center gap-2 font-display text-xl font-extrabold ${inverse ? 'text-white' : 'text-ink dark:text-white'}`}
    >
      <span className="grid size-9 place-items-center rounded-xl bg-brand-600 text-white">Q</span>
      QuizRoom
    </Link>
  );
}
export function Spinner() {
  return (
    <span
      className="inline-block size-5 animate-spin rounded-full border-2 border-current border-r-transparent"
      aria-label="Loading"
    />
  );
}
export function LoadingPage() {
  return (
    <main className="mx-auto max-w-6xl p-6" aria-busy="true">
      <div className="skeleton h-12 w-72" />
      <div className="mt-8 grid gap-5 md:grid-cols-3">
        {[1, 2, 3].map((x) => (
          <div key={x} className="skeleton h-52" />
        ))}
      </div>
    </main>
  );
}
export function ErrorState({
  message = 'We could not load this page.',
  retry,
}: {
  message?: string;
  retry?: () => void;
}) {
  return (
    <div className="card mx-auto my-16 max-w-lg p-8 text-center">
      <div className="mx-auto mb-4 grid size-12 place-items-center rounded-full bg-red-50 text-red-700">
        !
      </div>
      <h2 className="text-xl font-bold">Something went sideways</h2>
      <p className="mt-2 text-slate-600 dark:text-slate-300">{message}</p>
      {retry && (
        <button className="btn-primary mt-5" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}
export function EmptyState({
  icon: Icon = BookOpenCheck,
  title,
  body,
  action,
}: {
  icon?: any;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed p-10 text-center">
      <Icon className="mx-auto size-10 text-brand-600" />
      <h3 className="mt-4 text-lg font-bold">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">{body}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
export function Modal({
  title,
  body,
  confirm,
  confirmLabel = 'Confirm',
  tone = 'primary',
  onClose,
}: {
  title: string;
  body: string;
  confirm: () => void;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const controls = [
        ...dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => !element.hasAttribute('disabled'));
      if (!controls.length) return;
      const first = controls[0]!;
      const last = controls.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-body"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={dialogRef} className="card w-full max-w-md p-6">
        <h2 id="modal-title" className="text-xl font-bold">
          {title}
        </h2>
        <p id="modal-body" className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          {body}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button ref={cancelRef} className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className={tone === 'danger' ? 'btn-danger' : 'btn-primary'}
            onClick={() => {
              confirm();
              onClose();
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
export function Toasts() {
  const { toasts, removeToast } = useAppStore();
  return (
    <div
      className="fixed right-4 top-4 z-[60] flex w-[min(24rem,calc(100%-2rem))] flex-col gap-2"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <button
          key={t.id}
          onClick={() => removeToast(t.id)}
          className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold shadow-soft ${t.tone === 'error' ? 'bg-red-50 text-red-800' : t.tone === 'success' ? 'bg-brand-50 text-brand-700' : 'bg-white text-slate-700'}`}
        >
          {t.message}
        </button>
      ))}
    </div>
  );
}

// Shared query hook intentionally lives beside the auth shell it supports.
// eslint-disable-next-line react-refresh/only-export-components
export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<{ user: any | null }>('/auth/me'),
    retry: false,
  });
}
export function Protected() {
  const me = useMe();
  if (me.isLoading) return <LoadingPage />;
  if (!me.data?.user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (me.data.user.role !== 'ADMIN' && me.data.user.approvalStatus !== 'APPROVED')
    return <Navigate to="/approval-pending" replace />;
  return <Outlet />;
}

const baseNav = [
  { to: '/dashboard', label: 'Dashboard', icon: Home },
  { to: '/quizzes', label: 'My quizzes', icon: BookOpenCheck },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/classes', label: 'Classes', icon: Users },
  { to: '/question-bank', label: 'Question bank', icon: LibraryBig },
  { to: '/media', label: 'Media library', icon: Images },
  { to: '/settings', label: 'Settings', icon: Settings },
];

type ShellHelp = {
  key: string;
  title: string;
  summary: string;
  steps: [string, string, string];
};

const shellHelpForPath = (pathname: string): ShellHelp => {
  if (pathname.startsWith('/question-bank'))
    return {
      key: 'question-bank',
      title: 'Build a reusable question library',
      summary: 'Group questions by topic, then reuse independent copies in quizzes or assignments.',
      steps: [
        'Create a library or sub-library for each topic.',
        'Select questions together to move them in one step.',
        'Archived libraries and questions remain recoverable.',
      ],
    };
  if (pathname.startsWith('/media'))
    return {
      key: 'media',
      title: 'Keep media organized',
      summary: 'Review stored images, audio, and video before space becomes tight.',
      steps: [
        'Check used and available storage at the top.',
        'Preview an item before removing it.',
        'Deleting unused media frees space for future quizzes.',
      ],
    };
  if (/^\/classes\/[^/]+/.test(pathname))
    return {
      key: 'class-detail',
      title: 'Choose the right class activity',
      summary: 'Run one shared live quiz or assign randomized questions from a library.',
      steps: [
        'Use Live quiz when the whole room should move together.',
        'Use Randomized assignment for individual saved question sets.',
        'Class results and active rooms remain available below the activity controls.',
      ],
    };
  if (pathname.startsWith('/classes'))
    return {
      key: 'classes',
      title: 'Start with a class',
      summary: 'A class keeps its roster, access method, activities, and reports together.',
      steps: [
        'Open an existing class or create a new one.',
        'Import a roster to add many students together.',
        'Choose Live quiz or Randomized assignment inside the class.',
      ],
    };
  if (pathname.startsWith('/reports'))
    return {
      key: 'reports',
      title: 'Review classroom evidence',
      summary: 'Open a report to inspect participation, marks, and question-level responses.',
      steps: [
        'Use filters to locate the relevant class or quiz.',
        'Open a report for participant and question details.',
        'Export a workbook when you need an offline record.',
      ],
    };
  if (pathname.startsWith('/settings'))
    return {
      key: 'settings',
      title: 'Set workspace defaults',
      summary: 'Preferences here become the starting point for future QuizRoom activities.',
      steps: [
        'Review your profile and workspace name.',
        'Choose sensible defaults for new quizzes.',
        'Use the security section to manage passwords and sessions.',
      ],
    };
  if (pathname.startsWith('/admin'))
    return {
      key: 'admin',
      title: 'Maintain a trusted host workspace',
      summary: 'Review host access and platform health before making administrative changes.',
      steps: [
        'Confirm identity before approving a host.',
        'Transfer owned content before removing an account.',
        'Check capacity warnings before a large classroom event.',
      ],
    };
  return {
    key: pathname.startsWith('/quizzes') ? 'quizzes' : 'dashboard',
    title: pathname.startsWith('/quizzes') ? 'Manage your quiz library' : 'Your host workspace',
    summary: pathname.startsWith('/quizzes')
      ? 'Create, publish, host, archive, and restore quizzes from one place.'
      : 'Start a quiz, choose a class, or return to any room that is already open.',
    steps: pathname.startsWith('/quizzes')
      ? [
          'Publish a quiz before opening it to a class.',
          'Copy a played quiz before editing to protect reports.',
          'Use Archive or Bin to remove clutter without accidental loss.',
        ]
      : [
          'Use Create quiz for new content.',
          'Use Assign to choose a class for individual work.',
          'Check Rooms and lobbies before opening another live session.',
        ],
  };
};

const secondaryNavCopy: Record<string, string> = {
  '/question-bank': 'Organize reusable questions',
  '/media': 'Review files and storage',
  '/settings': 'Profile, defaults, and security',
  '/admin/hosts': 'Host approvals and platform controls',
};

function useDialogFocus(
  open: boolean,
  onClose: () => void,
  dialogRef: RefObject<HTMLElement | null>,
  initialFocusRef: RefObject<HTMLElement | null>,
  fallbackFocusRef?: RefObject<HTMLElement | null>,
) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (!open) return;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const fallbackFocus = fallbackFocusRef?.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    initialFocusRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const controls = [
        ...dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => !element.hasAttribute('disabled'));
      if (!controls.length) return;
      const first = controls[0]!;
      const last = controls.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused?.isConnected && previouslyFocused.getClientRects().length)
        previouslyFocused.focus();
      else fallbackFocus?.focus();
    };
  }, [dialogRef, fallbackFocusRef, initialFocusRef, open]);
}

export function HostShell() {
  const me = useMe();
  const nav =
    me.data?.user?.role === 'ADMIN'
      ? [...baseNav, { to: '/admin/hosts', label: 'Superadmin', icon: ShieldCheck }]
      : baseNav;
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<any>('/settings'),
    retry: false,
  });
  const systemStatus = useQuery({
    queryKey: ['system-status'],
    queryFn: () =>
      api<{
        announcement: string;
        maintenanceEnabled: boolean;
        maintenanceMessage: string;
      }>('/system/status'),
    staleTime: 30_000,
  });
  const q = useQueryClient();
  const navTo = useNavigate();
  const { theme, toggleTheme } = useAppStore();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [dismissedHelpKeys, setDismissedHelpKeys] = useState<Set<string>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('quizroom_dismissed_help') ?? '[]');
      return new Set(Array.isArray(saved) ? saved.filter((key) => typeof key === 'string') : []);
    } catch {
      return new Set();
    }
  });
  const moreDialogRef = useRef<HTMLDivElement>(null);
  const moreCloseRef = useRef<HTMLButtonElement>(null);
  const helpDialogRef = useRef<HTMLDivElement>(null);
  const helpCloseRef = useRef<HTMLButtonElement>(null);
  const headerHelpRef = useRef<HTMLButtonElement>(null);
  const help = shellHelpForPath(location.pathname);
  const secondaryNav = nav.slice(4);
  const moreIsActive = secondaryNav.some(
    ({ to }) => location.pathname === to || location.pathname.startsWith(`${to}/`),
  );
  const logout = useMutation({
    mutationFn: () => api('/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      q.setQueryData(['me'], { user: null });
      navTo('/login');
    },
  });
  useEffect(() => {
    window.scrollTo(0, 0);
    setMoreOpen(false);
    setHelpOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    try {
      localStorage.setItem('quizroom_dismissed_help', JSON.stringify([...dismissedHelpKeys]));
    } catch {
      // Dismissing remains available for this visit if storage is blocked.
    }
  }, [dismissedHelpKeys]);
  useEffect(() => {
    if (!moreOpen) return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeAtDesktop = (event: MediaQueryListEvent | MediaQueryList) => {
      if (event.matches) setMoreOpen(false);
    };
    closeAtDesktop(desktop);
    desktop.addEventListener('change', closeAtDesktop);
    return () => desktop.removeEventListener('change', closeAtDesktop);
  }, [moreOpen]);
  useDialogFocus(moreOpen, () => setMoreOpen(false), moreDialogRef, moreCloseRef, headerHelpRef);
  useDialogFocus(helpOpen, () => setHelpOpen(false), helpDialogRef, helpCloseRef, headerHelpRef);
  return (
    <div className="min-h-screen bg-cream dark:bg-slate-950">
      <a
        className="sr-only focus:not-sr-only focus:fixed focus:z-50 focus:bg-white focus:p-3"
        href="#main"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-white/95 px-4 backdrop-blur dark:bg-slate-900/95 lg:pl-[17rem]">
        <div className="lg:hidden">
          <Logo to="/dashboard" />
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            ref={headerHelpRef}
            className="btn-secondary !px-3"
            aria-label="Open help for this page"
            aria-haspopup="dialog"
            aria-expanded={helpOpen}
            onClick={() => {
              setMoreOpen(false);
              setHelpOpen(true);
            }}
          >
            <CircleHelp className="size-5" />
          </button>
          <button
            className="btn-secondary !px-3"
            onClick={toggleTheme}
            aria-label={`Use ${theme === 'light' ? 'dark' : 'light'} theme`}
          >
            {theme === 'light' ? <Moon className="size-5" /> : <Sun className="size-5" />}
          </button>
          <span className="ml-2 hidden text-right sm:block">
            <span className="block text-sm font-bold">{me.data?.user?.name}</span>
            <span className="block text-xs text-slate-500">
              {settings.data?.preferences?.workspaceName ?? 'Personal workspace'}
            </span>
          </span>
          <button
            className="btn-secondary !px-3"
            onClick={() => logout.mutate()}
            aria-label="Sign out"
          >
            <LogOut className="size-5" />
          </button>
        </div>
      </header>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 overflow-y-auto border-r bg-ink p-5 text-white lg:block">
        <div className="flex min-h-full flex-col">
          <Logo inverse to="/dashboard" />
          <p className="mt-8 px-3 text-xs font-bold uppercase tracking-widest text-white/45">
            Workspace
          </p>
          <nav className="mt-3 space-y-1">
            {nav.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `flex min-h-11 min-w-0 items-center gap-3 rounded-xl px-3 text-sm font-semibold ${isActive ? 'bg-white text-ink' : 'text-white/70 hover:bg-white/10 hover:text-white'}`
                }
              >
                <Icon className="size-5 shrink-0" />
                <span className="truncate">{label}</span>
              </NavLink>
            ))}
          </nav>
          <Link
            to="/quizzes/new"
            className="btn mt-6 w-full shrink-0 bg-brand-500 text-white hover:bg-brand-600"
          >
            <Plus className="size-5 shrink-0" />
            <span className="truncate">Create quiz</span>
          </Link>
          {!dismissedHelpKeys.has(help.key) && (
            <div className="mt-auto pt-6">
              <aside className="rounded-xl border border-white/10 bg-white/10 p-3 text-xs leading-relaxed text-white/75">
                <div className="flex items-start gap-2">
                  <CircleHelp className="mt-0.5 size-4 shrink-0 text-brand-300" />
                  <div className="min-w-0 flex-1">
                    <strong className="block text-white">{help.title}</strong>
                    <span className="mt-1 block">{help.summary}</span>
                  </div>
                  <button
                    className="grid size-7 shrink-0 place-items-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white"
                    onClick={() =>
                      setDismissedHelpKeys((current) => new Set(current).add(help.key))
                    }
                    aria-label={`Dismiss ${help.title}`}
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
                <button
                  className="mt-3 font-bold text-brand-200 underline-offset-4 hover:underline"
                  onClick={() => setHelpOpen(true)}
                >
                  Open quick help
                </button>
              </aside>
            </div>
          )}
        </div>
      </aside>
      <main id="main" className="pb-24 lg:ml-64 lg:pb-0">
        {systemStatus.data?.announcement && (
          <div className="border-b border-brand-200 bg-brand-50 px-5 py-3 text-center text-sm font-semibold text-brand-950">
            {systemStatus.data.announcement}
          </div>
        )}
        {systemStatus.data?.maintenanceEnabled && systemStatus.data.maintenanceMessage && (
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-3 text-center text-sm font-semibold text-amber-950">
            {systemStatus.data.maintenanceMessage}
          </div>
        )}
        <Outlet />
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-center justify-around border-t bg-white dark:bg-slate-900 lg:hidden">
        {nav.slice(0, 4).map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `grid min-w-16 place-items-center gap-0.5 text-[11px] font-bold ${isActive ? 'text-brand-700' : 'text-slate-500'}`
            }
          >
            <Icon className="size-5" />
            {label}
          </NavLink>
        ))}
        <button
          className={`grid min-w-16 place-items-center gap-0.5 text-[11px] font-bold ${
            moreIsActive ? 'text-brand-700' : 'text-slate-500'
          }`}
          onClick={() => {
            setHelpOpen(false);
            setMoreOpen(true);
          }}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          aria-controls="mobile-more-menu"
          aria-current={moreIsActive ? 'page' : undefined}
          aria-label={moreIsActive ? 'More tools, current section' : 'More tools'}
        >
          <Menu className="size-5" />
          More
        </button>
      </nav>
      {moreOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/55 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-more-title"
          onMouseDown={(event) => event.target === event.currentTarget && setMoreOpen(false)}
        >
          <div
            id="mobile-more-menu"
            ref={moreDialogRef}
            className="max-h-[85dvh] w-full overflow-y-auto rounded-t-3xl bg-cream p-5 pb-[calc(2rem+env(safe-area-inset-bottom))] text-ink shadow-2xl dark:bg-slate-950 dark:text-white"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">Workspace</p>
                <h2 id="mobile-more-title" className="mt-1 text-2xl font-extrabold">
                  More tools
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Question resources, settings, and administrative controls.
                </p>
              </div>
              <button
                ref={moreCloseRef}
                className="grid size-10 shrink-0 place-items-center rounded-xl border bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800"
                onClick={() => setMoreOpen(false)}
                aria-label="Close more tools"
              >
                <X className="size-5" />
              </button>
            </div>
            <Link
              to="/quizzes/new"
              className="btn-primary mt-5 w-full"
              onClick={() => setMoreOpen(false)}
            >
              <Plus className="size-5" /> Create quiz
            </Link>
            <nav className="mt-4 grid gap-3 sm:grid-cols-2" aria-label="More workspace tools">
              {secondaryNav.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    `flex min-h-20 items-center gap-3 rounded-2xl border p-4 transition ${
                      isActive
                        ? 'border-brand-400 bg-brand-50 text-brand-900 dark:border-brand-700 dark:bg-brand-950/30 dark:text-brand-100'
                        : 'bg-white hover:border-brand-300 dark:bg-slate-900 dark:hover:border-brand-700'
                    }`
                  }
                >
                  <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-slate-100 text-brand-700 dark:bg-slate-800 dark:text-brand-300">
                    <Icon className="size-5" />
                  </span>
                  <span className="min-w-0">
                    <strong className="block truncate">{label}</strong>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      {secondaryNavCopy[to] ?? 'Open workspace tool'}
                    </span>
                  </span>
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
      {helpOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-slate-950/55 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="shell-help-title"
          aria-describedby="shell-help-summary"
          onMouseDown={(event) => event.target === event.currentTarget && setHelpOpen(false)}
        >
          <div
            ref={helpDialogRef}
            className="card max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto p-6 sm:p-7"
          >
            <div className="flex items-start gap-4">
              <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-200">
                <CircleHelp className="size-6" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="eyebrow">Quick help</p>
                <h2 id="shell-help-title" className="mt-1 text-2xl font-extrabold">
                  {help.title}
                </h2>
              </div>
              <button
                ref={helpCloseRef}
                className="grid size-10 shrink-0 place-items-center rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => setHelpOpen(false)}
                aria-label="Close help"
              >
                <X className="size-5" />
              </button>
            </div>
            <p id="shell-help-summary" className="mt-4 text-slate-600 dark:text-slate-300">
              {help.summary}
            </p>
            <ol className="mt-5 space-y-3">
              {help.steps.map((step, index) => (
                <li
                  key={step}
                  className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800"
                >
                  <span className="grid size-7 shrink-0 place-items-center rounded-full bg-brand-100 text-sm font-extrabold text-brand-800 dark:bg-brand-950 dark:text-brand-200">
                    {index + 1}
                  </span>
                  <span className="pt-0.5 text-sm font-semibold">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                className="btn-secondary"
                onClick={() => {
                  setDismissedHelpKeys((current) => new Set(current).add(help.key));
                  setHelpOpen(false);
                }}
              >
                Dismiss page tip
              </button>
              <button className="btn-primary" onClick={() => setHelpOpen(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
